import { FC } from 'react';

import { Input, Link, RyeconMagnifyingGlass } from '@dbt-labs/sourdough';

import { ColumnCard } from '@/components/ColumnCard';
import { DescriptionChangesProvider } from '@/context/DescriptionChangesProvider';
import {
  AppliedColumn,
  AssetWithColumns,
  ResourceWithColumns,
  useAppliedColumns,
} from '@/hooks/appliedColumns';
import { useFuse } from '@/hooks/fuse';
import { useValidatedParams } from '@/hooks/params';

type FuseHit = {
  item: AppliedColumn;
  matches?: {
    key: string;
    value: string;
  }[];
};

interface ColumnsTableProps {
  columns: FuseHit[] | undefined;
  query: string;
  primaryKey?: string[];
  shouldShowLineage?: boolean;
}

const ColumnsTable: FC<ColumnsTableProps> = ({
  columns,
  query,
  primaryKey,
  shouldShowLineage = true,
}) => {
  const { projectId } = useValidatedParams();

  if (columns == null) {
    return <div className="text-fgDecorative">Loading...</div>;
  }

  if (!columns.length) {
    return (
      <div className="text-fgDecorative">
        <span>No columns found.</span>
        {/* below text copied verbatim from 'missingCatalogInfo' NotificationBanner in MissingDataHeader.tsx */}
        {/* TODO: determine whether to make this block of text shareable */}
        <p>
          Please ensure that this resource and{' '}
          <span className="font-mono text-xs">docs generate</span> are run within job(s)
          in your{' '}
          <Link
            isInternal={false}
            shouldOpenNewTab
            to="https://docs.getdbt.com/docs/deploy/deploy-environments#set-as-production-environment"
          >
            production
          </Link>{' '}
          or staging environment. See{' '}
          <Link
            isInternal={false}
            shouldOpenNewTab
            to="https://docs.getdbt.com/docs/collaborate/explore-projects#generate-metadata"
          >
            the docs
          </Link>{' '}
          for more information.
        </p>
      </div>
    );
  }
  return (
    <ul aria-label="Columns" className="space-y-2">
      {columns.map(({ item, matches }) => {
        const metaMatch = matches?.some((match) => match.key === 'metaString');
        const name = item.name?.toLowerCase();
        return (
          <DescriptionChangesProvider
            key={name}
            projectId={projectId}
            resourceIdentifier={name}
            isColumnLevel
          >
            <ColumnCard
              {...item}
              isPrimaryKey={primaryKey?.includes(name ?? '') || false}
              metaMatch={metaMatch ? query : undefined}
              shouldShowLineage={shouldShowLineage}
            />
          </DescriptionChangesProvider>
        );
      })}
    </ul>
  );
};
export interface ColumnsViewProps {
  resource: ResourceWithColumns | AssetWithColumns | undefined;
  shouldShowLineage?: boolean;
  className?: string;
}

export const ColumnsView: FC<ColumnsViewProps> = ({
  resource,
  shouldShowLineage = true,
  className = '',
}) => {
  const columns = useAppliedColumns(resource);
  const filteredColumns = useFuse(columns ?? [], {
    limit: Infinity,
    matchAllOnEmptyQuery: true,
    findAllMatches: true,
    ignoreLocation: true,
    threshold: 0.1,
    useExtendedSearch: true,
    includeMatches: true,
    keys: [
      { name: 'name', weight: 10 },
      { name: 'description', weight: 8 },
      { name: 'type', weight: 8 },
      { name: 'detail', weight: 6 },
      {
        name: 'tests.name.name',
        weight: 4,
      },
      // if meta is matched
      // not expanded "Includes metadata: 'search string'"
      // expanded highlight within existing metadata block
      {
        name: 'metaString',
        weight: 2,
      },
    ],
  });

  return (
    <div className={className}>
      <Input
        testId="columnSearch"
        name="Columns"
        className="my-4 mb-4 w-64"
        isEdit
        placeholder="Search for columns"
        startIcon={{ ryecon: RyeconMagnifyingGlass }}
        onChange={(e) => {
          filteredColumns.setQuery(e.target.value);
        }}
      />
      <ColumnsTable
        columns={filteredColumns.hits}
        query={filteredColumns.query}
        primaryKey={
          resource && 'catalog' in resource
            ? resource.catalog?.primaryKey || undefined
            : undefined
        }
        shouldShowLineage={shouldShowLineage}
      />
    </div>
  );
};
